import type {
  EvaluacionAdminResponse,
  EvaluacionRequisitoAdmin,
  EvaluacionTernaAdmin,
  EvidenciaAdmin,
  RequisitoAdmin,
} from '@/types/evaluacionAdmin';
import type { CalificacionType } from '@/types/types';
import { renderWithBold } from '@/utils/renderWithBold';
import React, { useMemo, useState } from 'react';

// ============================
// Types (same layout structures as MatrizEvaluacion)
// ============================

interface FilaEvaluacionRO {
  id: string;
  ternaId: number | null;
  grupo: string | null;
  celdas: Record<number, RequisitoAdmin | null>;
}

interface GrupoEvaluacionRO {
  id: string;
  filas: FilaEvaluacionRO[];
}

// ============================
// Numeral sorting (identical to MatrizEvaluacion)
// ============================

interface NumeralKey {
  isAnnex: boolean;
  annexLetter: string;
  parts: number[];
}

function parseNumeral(numeral?: string): NumeralKey {
  if (!numeral) return { isAnnex: false, annexLetter: '', parts: [Infinity] };
  const trimmed = numeral.trim();
  let isAnnex = false;
  let annexLetter = '';
  let rest = trimmed;

  const anexoMatch = trimmed.match(/^Anexo\s+([A-Za-z])/i);
  if (anexoMatch) {
    isAnnex = true;
    annexLetter = anexoMatch[1].toUpperCase();
    rest = trimmed.slice(anexoMatch[0].length).replace(/^\.?\s*/, '');
  } else if (/^[A-Za-z][.\s]/.test(trimmed)) {
    isAnnex = true;
    annexLetter = trimmed[0].toUpperCase();
    rest = trimmed.slice(1).replace(/^\.?\s*/, '');
  }

  const numMatch = rest.match(/^\d+(?:\.\d+)*/);
  const parts: number[] = [];
  if (numMatch) {
    for (const p of numMatch[0].split('.')) {
      const n = parseInt(p, 10);
      if (!isNaN(n)) parts.push(n);
    }
  }
  if (parts.length === 0) parts.push(isAnnex ? 0 : Infinity);
  return { isAnnex, annexLetter, parts };
}

// ============================
// Build groups (adapted from admin response)
// ============================

/** Columna de NTC para la grilla */
interface ColumnaNtcRO {
  ntcId: number;
  nombre: string;
  orden: number;
}

function buildGruposDesdeAdmin(data: EvaluacionAdminResponse): GrupoEvaluacionRO[] {
  // Use combinacion.ntcs for column definition when available
  const columnas: ColumnaNtcRO[] =
    data.combinacion?.ntcs ?? data.ntcs.map((n) => ({ ntcId: n.ntcId, nombre: n.nombre, orden: n.orden ?? 0 }));

  const ntcOrdenMap = new Map<number, number>();
  for (const col of columnas) ntcOrdenMap.set(col.ntcId, col.orden);

  const reqMap = new Map<string, RequisitoAdmin>();
  for (const r of data.requisitos) reqMap.set(r.requisitoId, r);

  const ordenes = columnas.map((c) => c.orden).sort((a, b) => a - b);
  const filasPorOrden = new Map<number, FilaEvaluacionRO[]>();

  for (const terna of data.ternas) {
    const celdas: Record<number, RequisitoAdmin | null> = {};
    for (const o of ordenes) celdas[o] = null;

    if (terna.requisitoC1Id) celdas[1] = reqMap.get(terna.requisitoC1Id) || null;
    if (terna.requisitoC2Id) celdas[2] = reqMap.get(terna.requisitoC2Id) || null;
    if (terna.requisitoC3Id) celdas[3] = reqMap.get(terna.requisitoC3Id) || null;

    const fila: FilaEvaluacionRO = {
      id: terna.id.toString(),
      ternaId: terna.id,
      grupo: terna.grupo,
      celdas,
    };

    if (!filasPorOrden.has(terna.orden)) filasPorOrden.set(terna.orden, []);
    filasPorOrden.get(terna.orden)!.push(fila);
  }

  const grupos: GrupoEvaluacionRO[] = Array.from(filasPorOrden.entries())
    .sort(([a], [b]) => a - b)
    .map(([orden, filas]) => ({
      id: `grupo-${orden}`,
      filas: filas.sort((a, b) => {
        if (a.grupo && !b.grupo) return -1;
        if (!a.grupo && b.grupo) return 1;
        if (a.grupo && b.grupo && a.grupo !== b.grupo) return a.grupo.localeCompare(b.grupo);
        return (a.ternaId ?? Number(a.id)) - (b.ternaId ?? Number(b.id));
      }),
    }));

  return grupos;
}

// ============================
// Span calculation (identical to MatrizEvaluacion)
// ============================

function calcularSpansRO(filas: FilaEvaluacionRO[], ordenes: number[]) {
  const totalRows = filas.length;
  const spans: Record<number, { startRow: number; span: number; req: RequisitoAdmin | null }[]> = {};

  for (const orden of ordenes) {
    spans[orden] = [];
    let i = 0;
    while (i < totalRows) {
      const req = filas[i].celdas[orden] ?? null;
      let j = i + 1;
      while (j < totalRows && req !== null && filas[j].celdas[orden]?.requisitoId === req.requisitoId) {
        j++;
      }
      spans[orden].push({ startRow: i, span: j - i, req });
      i = j;
    }
  }

  return spans;
}

interface EvalTernaSpanRO {
  startRow: number;
  span: number;
  evalKey: string | null;
}

function calcularEvalSpansRO(filas: FilaEvaluacionRO[]): EvalTernaSpanRO[] {
  const totalRows = filas.length;
  const spans: EvalTernaSpanRO[] = [];
  let i = 0;

  while (i < totalRows) {
    const fila = filas[i];
    if (fila.ternaId == null) {
      spans.push({ startRow: i, span: 1, evalKey: null });
      i++;
    } else if (fila.grupo) {
      const evalKey = `grupo:${fila.grupo}`;
      let j = i + 1;
      while (j < totalRows && filas[j].grupo === fila.grupo) j++;
      spans.push({ startRow: i, span: j - i, evalKey });
      i = j;
    } else {
      spans.push({ startRow: i, span: 1, evalKey: `terna:${fila.ternaId}` });
      i++;
    }
  }

  return spans;
}

// ============================
// Calificación badge (read-only)
// ============================

const calColors: Record<CalificacionType, string> = {
  C: 'bg-green-100 text-green-800 border-green-300',
  NC: 'bg-red-100 text-red-800 border-red-300',
  CP: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  NA: 'bg-gray-100 text-gray-600 border-gray-300',
};

function CalificacionBadge({ cal }: { cal: CalificacionType }) {
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-semibold border ${calColors[cal]}`}>{cal}</span>;
}

// ============================
// Read-only requisito cell
// ============================

const RequisitoCellRO = React.memo<{
  req: RequisitoAdmin;
  evaluacion?: EvaluacionRequisitoAdmin;
  expandAll?: boolean;
}>(({ req, evaluacion, expandAll }) => {
  const [descExpanded, setDescExpanded] = useState(false);
  const isExpanded = expandAll || descExpanded;

  const currentCal = evaluacion?.calificacion ?? 'NC';
  const evalCal = evaluacion?.evaluacionRequisito?.calificacion;

  return (
    <div className="flex flex-col justify-start">
      <div className="font-bold text-sm text-gray-900">{req.capitulo}</div>
      <div className="font-bold text-sm text-gray-900">{req.numeral}</div>
      <div className={`text-sm text-gray-700 mt-1 whitespace-pre-wrap ${isExpanded ? '' : 'line-clamp-3'}`}>
        {renderWithBold(req.descripcion)}
      </div>
      {!expandAll && (
        <button
          type="button"
          onClick={() => setDescExpanded((v) => !v)}
          className="text-xs text-blue-600 hover:text-blue-800 font-medium mt-0.5 self-start"
        >
          {descExpanded ? 'Ver menos' : 'Ver más'}
        </button>
      )}
      <div className="mt-2 flex items-center gap-2">
        <CalificacionBadge cal={currentCal} />
        {evalCal && (
          <>
            <span className="text-gray-400">→</span>
            <CalificacionBadge cal={evalCal} />
          </>
        )}
      </div>
    </div>
  );
});
RequisitoCellRO.displayName = 'RequisitoCellRO';

// ============================
// Read-only evaluación terna
// ============================

const EvaluacionTernaRO = React.memo<{
  evalTerna: EvaluacionTernaAdmin;
  evidencias: EvidenciaAdmin[];
}>(({ evalTerna, evidencias }) => {
  const linkedEvidencias = evidencias.filter((ev) => evalTerna.evidenciaIds.includes(ev.evidenciaId));

  return (
    <div className="w-full h-full space-y-3">
      {/* Observaciones */}
      {evalTerna.observaciones && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Observaciones</div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-2">{evalTerna.observaciones}</p>
        </div>
      )}

      {/* Plan de Mejora */}
      {evalTerna.planMejora && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Plan de Mejora</div>
          <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-2">{evalTerna.planMejora}</p>
        </div>
      )}

      {/* Fecha Implementación */}
      {evalTerna.fechaImplementacion && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Fecha Implementación</div>
          <p className="text-sm text-gray-700">
            {new Date(evalTerna.fechaImplementacion).toLocaleDateString('es-CO', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            })}
          </p>
        </div>
      )}

      {/* Evidencias vinculadas */}
      {linkedEvidencias.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Evidencias</div>
          <div className="space-y-1">
            {linkedEvidencias.map((ev) => (
              <a
                key={ev.evidenciaId}
                href={ev.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                <span className="truncate">{ev.nombre}</span>
                {ev.estado === 'PENDIENTE' && (
                  <span className={'text-[10px] px-1.5 py-0.5 rounded font-medium bg-green-100 text-green-700'}>Nueva</span>
                )}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!evalTerna.observaciones && !evalTerna.planMejora && !evalTerna.fechaImplementacion && linkedEvidencias.length === 0 && (
        <p className="text-sm text-gray-400 italic">Sin información de evaluación</p>
      )}
    </div>
  );
});
EvaluacionTernaRO.displayName = 'EvaluacionTernaRO';

// ============================
// Main component
// ============================

interface MatrizEvaluacionAdminProps {
  data: EvaluacionAdminResponse;
  className?: string;
}

export function MatrizEvaluacionAdmin({ data, className = '' }: MatrizEvaluacionAdminProps) {
  // Use combinacion.ntcs for column definition when available, ntcs for summary
  const columnas = useMemo(() => {
    if (data.combinacion?.ntcs) return [...data.combinacion.ntcs].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));
    return [...data.ntcs]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map((n) => ({ ntcId: n.ntcId, nombre: n.nombre, orden: n.orden ?? 0 }));
  }, [data.combinacion, data.ntcs]);
  const ordenes = useMemo(() => columnas.map((c) => c.orden), [columnas]);
  const [expandAll, setExpandAll] = useState(false);

  // Build calificacion map: requisitoId -> EvaluacionRequisitoAdmin
  const calMap = useMemo(() => {
    const m = new Map<string, EvaluacionRequisitoAdmin>();
    for (const ev of data.evaluacion) m.set(ev.requisitoId, ev);
    return m;
  }, [data.evaluacion]);

  // Build a map from ternaId -> evalKey based on the current layout (ternas) so we always match the UI key
  const ternaIdToEvalKey = useMemo(() => {
    const m = new Map<number, string>();
    for (const terna of data.ternas) {
      m.set(terna.id, terna.grupo ? `grupo:${terna.grupo}` : `terna:${terna.id}`);
    }
    return m;
  }, [data.ternas]);

  // Build evalTerna map: evalKey -> EvaluacionTernaAdmin
  const evalTernaMap = useMemo(() => {
    const m = new Map<string, EvaluacionTernaAdmin>();
    for (const et of data.evaluacionesTernas) {
      let key: string | null = null;
      if (et.ternaId != null && ternaIdToEvalKey.has(et.ternaId)) {
        key = ternaIdToEvalKey.get(et.ternaId)!;
      } else if (et.grupo) {
        key = `grupo:${et.grupo}`;
      } else if (et.ternaId != null) {
        key = `terna:${et.ternaId}`;
      }
      if (key) {
        m.set(key, et);
      }
    }
    return m;
  }, [data.evaluacionesTernas, ternaIdToEvalKey]);

  // Build groups
  const grupos = useMemo(() => buildGruposDesdeAdmin(data), [data]);

  const gridCols = columnas.map(() => 'minmax(250px, 1fr)').join(' ') + ' minmax(350px, 1.5fr)';

  // Flatten all filas
  const flatFilas = useMemo(() => {
    const rows: { fila: FilaEvaluacionRO; grupoIdx: number; isLastInGrupo: boolean }[] = [];
    for (let gi = 0; gi < grupos.length; gi++) {
      const grupo = grupos[gi];
      for (let fi = 0; fi < grupo.filas.length; fi++) {
        rows.push({
          fila: grupo.filas[fi],
          grupoIdx: gi,
          isLastInGrupo: fi === grupo.filas.length - 1,
        });
      }
    }
    return rows;
  }, [grupos]);

  const allFilas = useMemo(() => flatFilas.map((r) => r.fila), [flatFilas]);
  const globalSpans = useMemo(() => calcularSpansRO(allFilas, ordenes as number[]), [allFilas, ordenes]);
  const globalEvalSpans = useMemo(() => calcularEvalSpansRO(allFilas), [allFilas]);
  const totalRows = flatFilas.length;

  const getBorderBottom = (spanEnd: number) => {
    if (spanEnd >= totalRows) return undefined;
    const lastRow = flatFilas[spanEnd - 1];
    if (lastRow?.isLastInGrupo) return '3px solid #9ca3af';
    return '1px solid #e5e7eb';
  };

  // NTC summary row (uses cumplimiento data, not column definition)
  const ntcSummary = useMemo(() => {
    return [...data.ntcs]
      .sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0))
      .map((ntc) => ({
        nombre: ntc.nombre,
        totalC: ntc.totalC,
        totalNC: ntc.totalNC,
        totalCP: ntc.totalCP,
        totalNA: ntc.totalNA,
        total: ntc.totalRequisitos,
        pct: ntc.porcentajeCumplimiento ?? 0,
        cumple: ntc.cumpleNTC,
      }));
  }, [data.ntcs]);

  return (
    <div className={`space-y-4 flex flex-col min-w-0 ${className}`}>
      {/* NTC summary cards */}
      <div className="shrink-0 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ntcSummary.map((ntc) => (
          <div
            key={ntc.nombre}
            className={`rounded-lg border p-4 ${ntc.cumple ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm text-gray-800">{ntc.nombre}</span>
              <span
                className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                  ntc.cumple ? 'bg-green-200 text-green-800' : 'bg-gray-200 text-gray-600'
                }`}
              >
                {(ntc.pct ?? 0).toFixed(1)}%
              </span>
            </div>
            <div className="flex gap-3 text-xs text-gray-600">
              <span className="text-green-700">C: {ntc.totalC}</span>
              <span className="text-red-700">NC: {ntc.totalNC}</span>
              <span className="text-yellow-700">CP: {ntc.totalCP}</span>
              <span className="text-gray-500">NA: {ntc.totalNA}</span>
              <span className="text-gray-400">Total: {ntc.total}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Matrix grid */}
      <div className="flex-1 min-h-0 min-w-0 overflow-auto w-full border border-gray-300 rounded-lg">
        <div style={{ display: 'table', minWidth: '100%' }}>
          {/* Header */}
          <div
            className="sticky grid z-20 bg-gray-100 border-b-2 border-gray-300 shadow-sm"
            style={{
              gridTemplateColumns: gridCols,
            }}
          >
            {columnas.map((col, idx) => (
              <div key={col.orden} className="p-4 font-semibold text-gray-800 flex items-center justify-between">
                <span>{col.nombre}</span>
                {idx === 0 && (
                  <button
                    type="button"
                    onClick={() => setExpandAll((v) => !v)}
                    className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
                  >
                    {expandAll ? 'Colapsar' : 'Expandir'}
                  </button>
                )}
              </div>
            ))}
            <div className="p-4 font-semibold text-gray-800">Evaluación</div>
          </div>

          {/* Rows */}
          {totalRows > 0 ? (
            <div
              className="grid"
              style={{
                gridTemplateColumns: gridCols,
                gridTemplateRows: `repeat(${totalRows}, auto)`,
              }}
            >
              {/* NTC columns */}
              {ordenes.map((orden: any, colIdx: number) =>
                globalSpans[orden as number].map((item: any, i: number) => {
                  const spanEnd = item.startRow + item.span;
                  return (
                    <div
                      key={`ntc${orden}-${i}`}
                      className="p-4 bg-white border-r border-gray-200"
                      style={{
                        gridColumn: colIdx + 1,
                        gridRow: `${item.startRow + 1} / span ${item.span}`,
                        borderBottom: getBorderBottom(spanEnd),
                      }}
                    >
                      {item.req ? (
                        <RequisitoCellRO req={item.req} evaluacion={calMap.get(item.req.requisitoId)} expandAll={expandAll} />
                      ) : (
                        <div className="text-gray-400 text-sm italic">-</div>
                      )}
                    </div>
                  );
                })
              )}

              {/* Evaluación column */}
              {globalEvalSpans.map((item, idx) => {
                const spanEnd = item.startRow + item.span;
                const evalTerna = item.evalKey ? evalTernaMap.get(item.evalKey) : null;

                return (
                  <div
                    key={item.evalKey ? `eval-${item.evalKey}` : `eval-empty-${idx}`}
                    className="p-4 bg-white"
                    style={{
                      gridColumn: ordenes.length + 1,
                      gridRow: `${item.startRow + 1} / span ${item.span}`,
                      borderBottom: getBorderBottom(spanEnd),
                    }}
                  >
                    {evalTerna ? (
                      <EvaluacionTernaRO evalTerna={evalTerna} evidencias={data.evidencias} />
                    ) : item.evalKey ? (
                      <p className="text-sm text-gray-400 italic">Sin evaluación registrada</p>
                    ) : (
                      <div className="text-gray-400 text-sm italic">Sin terna</div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-8 text-center text-gray-400">No hay requisitos para evaluar</div>
          )}
        </div>
      </div>
    </div>
  );
}
